<template>
  <div class="relative">
    <div class="flex h-13">
      <div
        v-if="!entry.is_baseline && rearrangeState"
        class="absolute z-20 flex items-center justify-center h-6 border rounded-full cursor-move handle -top-3 w-30 border-light-purple bg-pure-white"
        :style="{ left: '50%', transform: 'translateX(-50%)' }"
      >
        <BlIcon
          icon="drag"
          class="flex-shrink-0 w-4"
          :style="{ transform: 'rotate(90deg)' }"
        />
      </div>
      <div class="sticky left-0 z-10 flex pl-6 bg-pure-white"></div>
      <div
        class="flex items-center flex-grow gap-6 w-200"
        :class="{ 'border-t border-slate-400': !entry.is_baseline }"
      >
        <div
          v-if="entry.is_baseline"
          class="w-full bg-grey-200 h-0.5 rounded-full"
        />
        <div v-else class="flex items-center justify-between w-full">
          <div
            v-for="n in 8"
            :key="n"
            class="bg-grey-200 h-0.5 w-8 rounded-full"
          />
        </div>
        <div class="flex items-center flex-shrink-0 gap-2">
          <div
            v-if="!isPrompting"
            class="flex-shrink-0 w-5 h-5 rounded-full"
            :style="{ backgroundColor: phaseLineColors[entry.line_color] }"
          />
          <BlTextSubtitle2 :text="entry.line_label" class="text-grey-400" />
          <BlIcon
            v-if="entry.is_baseline && rearrangeState"
            class="flex-shrink-0 w-5 h-5"
            icon="info"
            tooltip="This phase cannot be moved."
          />
        </div>
        <div
          v-if="entry.is_baseline"
          class="w-full bg-grey-200 h-0.5 rounded-full"
        />
        <div v-else class="flex items-center justify-between w-full">
          <div
            v-for="n in 8"
            :key="n"
            class="bg-grey-200 h-0.5 w-8 rounded-full"
          />
        </div>
      </div>
      <div class="sticky right-0 z-10 flex pr-6 bg-pure-white">
        <div
          class="relative flex items-center flex-shrink-0 w-12 px-2 transition-all duration-300 bg-pure-white"
          :class="{ 'border-t border-slate-400': !entry.is_baseline }"
        >
          <div id="entry-menu" class="relative">
            <BlInputButton
              @clicked="menuOpened = !menuOpened"
              icon="dots-vertical"
              class="flex items-center justify-center w-6 h-6 hover:bg-hover-state"
              type="plain"
            />
            <div
              v-if="menuOpened"
              class="absolute top-0 z-50 flex flex-col p-1 space-y-1 border rounded right-8 bg-pure-white border-grey-200"
            >
              <button
                v-if="entry.line_description"
                @click.prevent="descriptionOpened = true"
                class="px-4 py-2 text-left rounded outline-none hover:bg-hover-state w-44"
              >
                <BlTextBody2 class="text-slate-1000" text="View description" />
              </button>
              <button
                @click.prevent="editOpened = true"
                class="px-4 py-2 text-left rounded outline-none hover:bg-hover-state w-44"
              >
                <BlTextBody2 class="text-slate-1000" text="Edit phase" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <BlBackdrop
      v-if="descriptionOpened"
      label="Close"
      cancelLabel="Edit"
      @backdrop-close="
        descriptionOpened = false;
        editOpened = true;
      "
      @backdrop-action="descriptionOpened = false"
    >
      <div
        class="relative px-8 overflow-y-auto"
        :style="{ maxHeight: 'calc(100vh - 14rem)' }"
      >
        <div class="flex flex-col">
          <div
            class="sticky top-0 z-10 flex items-center gap-3 pb-4 bg-pure-white"
          >
            <div
              v-if="!isPrompting"
              class="flex-shrink-0 w-6 h-6 rounded-full"
              :style="{ backgroundColor: phaseLineColors[entry.line_color] }"
            />
            <BlTextTitle4 :text="entry.line_label" />
          </div>
          <BlTextBody2
            :text="entry.line_description"
            class="flex-wrap break-words w-136"
          />
        </div>
      </div>
    </BlBackdrop>
    <BlBackdrop
      v-if="editOpened && !deleteOpened"
      title="Edit phase line"
      label="Update"
      :disabled="!labelInput.model || submitLoading"
      @backdrop-close="editOpened = false"
      @backdrop-action="onSave"
    >
      <div class="relative flex flex-col gap-4 px-8">
        <div class="flex gap-4">
          <BlInputText :input="labelInput" @changed="inputHandler" />
          <div v-if="!isPrompting" class="relative">
            <label class="relative mb-1">
              <BlTextBody3 text="Color" />
            </label>
            <div
              :data-id="`phase-line-color`"
              :class="colorOpened ? 'border-light-purple ' : 'border-grey-200 '"
              class="relative flex items-center justify-between w-24 h-10 px-4 border rounded cursor-pointer"
              @click.prevent="colorOpened = !colorOpened"
            >
              <div
                class="flex-shrink-0 w-6 h-6 rounded-full"
                :style="{ backgroundColor: phaseLineColors[colorInput] }"
              />
              <BlIcon
                :data-id="`phase-line-color`"
                class="absolute w-5 h-5 transition-all duration-300 right-4"
                icon="chevron-down"
                :style="colorOpened ? 'rotate: 180deg' : ''"
              />
            </div>
            <div
              :data-id="`phase-line-color`"
              v-if="colorOpened"
              class="absolute right-0 z-10 py-4 pl-4 mt-2 border rounded shadow-md w-38 max-h-48 bg-pure-white border-grey-200"
            >
              <div :data-id="`phase-line-color`">
                <div :data-id="`phase-line-color`" class="flex flex-wrap gap-2">
                  <button
                    v-for="color in Object.keys(phaseLineColors)"
                    :key="color"
                    class="flex-shrink-0 w-6 h-6 border-2 rounded outline-none"
                    :class="
                      color === colorInput
                        ? 'border-light-purple'
                        : 'border-transparent'
                    "
                    :style="{ backgroundColor: phaseLineColors[color] }"
                    @click.prevent="colorInput = color"
                    :title="color"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <BlInputText :input="descriptionInput" @changed="inputHandler" />
        <div class="absolute z-10 left-8 -bottom-14">
          <BlTooltip
            :tooltip="entry.is_baseline ? `Baseline can't be deleted` : ''"
          >
            <BlInputButton
              type="plain-error"
              icon="trash"
              label="Delete phase line"
              :disabled="entry.is_baseline"
              @clicked="deleteOpened = true"
            />
          </BlTooltip>
        </div>
      </div>
    </BlBackdrop>
    <BlBackdrop
      v-if="deleteOpened"
      title="Delete the phase line"
      label="Delete"
      description="Are you sure you want to delete this phase line? Deleting it will only remove the phase line itself and won't affect your data."
      type="error"
      empty
      :disabled="submitLoading"
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onRemove"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";

import BlInputButton from "../../BlInputButton.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlBackdrop from "../../BlBackdrop.vue";
import BlInputText from "../../BlInputText.vue";
import BlIcon from "../../BlIcon.vue";
import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTooltip from "../../BlTooltip.vue";

export default {
  name: "TargetEntryPhaseLine",
  components: {
    BlInputButton,
    BlTextSubtitle2,
    BlTextBody3,
    BlBackdrop,
    BlInputText,
    BlIcon,
    BlTextTitle4,
    BlTextBody2,
    BlTooltip
  },
  props: ["entry", "rearrangeState", "isPrompting"],
  data() {
    return {
      submitLoading: false,
      menuOpened: false,
      descriptionOpened: false,
      editOpened: false,
      deleteOpened: false,
      labelInput: {
        label: "Label",
        asterisk: true,
        placeholder: "Type the label for the phase line",
        model: this.entry.line_label
      },
      colorOpened: false,
      colorInput: this.entry.line_color,
      phaseLineColors: {
        lake: "#2AB1A8",
        tangerine: "#F57E00",
        cherry: "#FF4447",
        sky: "#487ADF",
        moss: "#A7C957",
        gold: "#F4D430",
        salmon: "#F4855D",
        primary: "#9A8AC2"
      },
      descriptionInput: {
        label: "Description",
        placeholder: "Type the phase description",
        textarea: true,
        model: this.entry.line_description
      }
    };
  },
  watch: {
    menuOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      }
    },
    editOpened(val) {
      if (val) {
        this.labelInput.model = this.entry.line_label;
        this.colorInput = this.entry.line_color;
        this.descriptionInput.model = this.entry.line_description;
      }
    },
    colorOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandlerColor);
        }, 10);
      }
    }
  },
  methods: {
    ...mapActions(["updatePhaseLine", "deletePhaseLine"]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    onSave() {
      this.submitLoading = true;
      const data = {
        id: this.entry.id,
        line_label: this.labelInput.model,
        line_color: this.colorInput,
        line_description: this.descriptionInput.model,
        target_id: this.entry.target_id
      };
      this.updatePhaseLine(data).then(() => {
        this.submitLoading = false;
        this.editOpened = false;
      });
    },
    onRemove() {
      this.submitLoading = true;
      this.deletePhaseLine({
        id: this.entry.id,
        target_id: this.entry.target_id
      }).then(() => {
        this.submitLoading = false;
        this.deleteOpened = false;
        this.editOpened = false;
      });
    },
    closeHandler() {
      window.removeEventListener("click", this.closeHandler);
      this.menuOpened = false;
    },
    closeHandlerColor(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (`phase-line-color` === id) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closeHandlerColor);
        this.colorOpened = false;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/target/EntryPhaseLine.vue